import * as Turbo from "@hotwired/turbo"

export function url(input) {
  try {
    if (typeof input === 'object' && input.href) {
      return new URL(input);
    } else if (typeof input === 'string' && input.startsWith('/')) {
      return new URL(input, window.location.href);
    } else if (/(http|https):\/\//.test(input)) {
      return new URL(input);
    }
  } catch {
    // invalid URL
  }
  return null;
}

export function params(loc = document.location.href) {
  return new URLSearchParams(url(loc).search);
}

export function visit(params, path = document.location.pathname) {
  if (!params || params.toString() === '') {
    Turbo.visit(path)
  } else {
    Turbo.visit(`${path}?${params}`)
  }
}

export function visitPush(params, path = window.location) {
  const u = url(path)
  u.search = params.toString()
  window.history.replaceState({internalPushPath: u.pathname}, '', u)

  urlChanged(u)
}

function urlChanged(url) {
  const urlChangedEvent = new CustomEvent('urlchanged', { detail: { url } });
  window.dispatchEvent(urlChangedEvent);
}

window.addEventListener("popstate", () => {
  urlChanged(new URL(window.location))
})

export function checkForInternalPushstateRefresh() {
  window.addEventListener("popstate", (evt) => {
    if (!evt.state) return;
    const currentPagePath = document.querySelector('meta[name="internal-pushstate"]')?.content
    const {internalPushPath} = evt.state
    if(internalPushPath && currentPagePath !== internalPushPath) {
      Turbo.visit(window.location.href, { action: "replace" });
    }
  })
}

export function update(params) {
  const url = new URL(window.location)
  const newSearch = params.toString()

  if (url.search === `?${newSearch}`) return

  url.search = newSearch
  window.history.pushState({ internalPushPath: url.pathname }, '', url)
  urlChanged(url)
}

export function replace(params) {
  const url = new URL(window.location)
  const newSearch = params.toString()

  if (url.search === `?${newSearch}`) return

  url.search = newSearch
  window.history.replaceState({ internalPushPath: url.pathname }, '', url)
  urlChanged(url)
}

export function newWindow(params, location = window.location) {
  const u = url(location)
  u.search = params.toString()
  window.open(u.href, '_blank');
}

function affect(key, value, fn) {
  const p = params()
  setParam(p, key, value)
  fn(p)
}

export function slice(keys) {
  let found = new URLSearchParams();
  let allParams = params();

  keys.forEach(key => {
    if(allParams.has(key)) {
      found.set(key, allParams.get(key))
    }
  })

  return found
}

export function sliceRemove(keys) {
  let found = {}
  let matches = new Set(keys)
  let allParams = params()

  for (const [key, value] of Array.from(allParams.entries())) {
    if(matches.has(key)) {
      allParams.delete(key)
      found[key] = value
    }
  }

  replace(allParams)
  return found
}

export function getParam(key) {
  return params().get(key)
}

function setParam(params, key, value) {
  if (value === null) {
    params.delete(key)
  } else if (typeof value === "object") {
    params.set(key, JSON.stringify(value))
  } else {
    params.set(key, value)
  }
}

// Merge both plain objects & URLSearchParams
export function mergeParams(obj, p = params()) {
  let entries
  if(obj instanceof URLSearchParams) {
    entries = obj.entries()
  } else {
    entries = Object.entries(obj)
  }

  for(const [k, v] of entries) {
    setParam(p, k, v)
  }

  return p
}

export function paramsToObject(p = params()) {
  let obj = {}
  for (const [key, value] of p.entries()) {
    obj[key] = value
  }
  return obj
}

export function removeParams(keys, p = params()) {
  keys.forEach(key => p.delete(key))
  return p
}

export function visitParam(key, value) {
  affect(key, value, visit)
}

export function updateParam(key, value) {
  affect(key, value, update)
}

export function replaceParam(key, value) {
  affect(key, value, replace)
}

export function updateParamInNewWindow(key, value) {
  affect(key, value, newWindow)
}

// Base function to handle requests with specific URL and method
export function turboFormVisit(url, method = "GET") {
  const upperMethod = method.toUpperCase()
  if (!upperMethod || upperMethod === "GET") {
    visit(null, url)
    return
  }

  // Build a one-off form
  const form = document.createElement("form")
  form.method = "post"
  form.action = url

  // Rails-style hidden _method
  const methodInput = document.createElement("input")
  methodInput.type = "hidden"
  methodInput.name = "_method"
  methodInput.value = upperMethod
  form.appendChild(methodInput)

  // CSRF token
  const token = document.querySelector("meta[name=csrf-token]")?.content
  if (token) {
    const tokenInput = document.createElement("input")
    tokenInput.type = "hidden"
    tokenInput.name = "authenticity_token"
    tokenInput.value = token
    form.appendChild(tokenInput)
  }

  document.body.appendChild(form)
  Turbo.navigator.submitForm(form)
  form.remove()
}

// Turbo does not expose a way to visit with a method other than GET
// or POST, so we have to build a form and submit it.
export function turboHandleMethod(link) {
  const method = link.dataset.turboMethod
  turboFormVisit(link.href, method)
}

export function validUrl(value) {
  const parsedUrl = url(value)
  return !!parsedUrl && (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:')
}

export function safeOpenUrl(value) {
  if (validUrl(value)) {
    window.open(url(value).href, '_blank', 'noopener,noreferrer')
  } else {
    const parsedUrl = url(value)
    if (parsedUrl) {
      console.error(`Cannot open URL with protocol: ${parsedUrl.protocol}`)
    } else {
      console.error(`Invalid URL: ${value}`)
    }
  }
}

export default {
  getParam, params, visit, visitParam, updateParam, slice,
  mergeParams, update, replace, sliceRemove, replaceParam,
  checkForInternalPushstateRefresh, visitPush, url,
  newWindow, updateParamInNewWindow, removeParams,
  paramsToObject, turboHandleMethod, turboFormVisit, safeOpenUrl, validUrl
}
